// A gated tool's approval card. The tool declares a ``ToolApproval`` (backend
// tools in their MCP ``meta``, Genie frontend tools on their display); the
// stream stamps it on the call, and ``viewFromApproval`` reads the call's args
// into what the card draws.

import { UUID_RE } from "@/utils/generic";
import { isRecord } from "@/utils/isRecord";
import type {
  ConfigCodeView,
  ConfigEntityRef,
  ConfigModelSelect,
  ConfigNumber,
  ConfigQuote,
  ConfigRowsLookup,
  ConfigRowsPreview,
  ConfigSchedule,
  ConfigSelect,
  ConfigText,
  ConfigToggle,
} from "@/types/api/spotflow/spotflow-config";

export type StepOp =
  | "add"
  | "edit"
  | "remove"
  | "run"
  | "send"
  | "go"
  | "save"
  | "link"
  | "on"
  | "off";

export type EntityKind = ConfigEntityRef["kind"];

// ``id`` is resolved to the entity's current name, falling back to ``name``
// (once the entity is gone) and then to the kind's noun ("Agent"), so a raw id
// never reaches the card.
export interface EntityRef {
  kind: EntityKind;
  id?: string;
  name?: string;
  // A list ``name_arg`` (row ids) or a ``count_arg``: the card counts it
  // ("2 rows", "3 conversations").
  count?: number;
  // The id of the entity it lives in: a version's or deployment's agent, a
  // column's or rows' table. It is read there, and opens there.
  parent?: string;
}

export type FieldDisplay =
  | (ConfigText & { type: "text" })
  | (ConfigNumber & { type: "number" })
  | (ConfigToggle & { type: "toggle" })
  | (ConfigSelect & { type: "select" })
  | (ConfigEntityRef & { type: "entity_ref" })
  | (ConfigCodeView & { type: "code_view" })
  | (ConfigQuote & { type: "quote" })
  | (ConfigRowsPreview & { type: "rows_preview" })
  | (ConfigModelSelect & { type: "model_select" })
  | (ConfigRowsLookup & { type: "rows_lookup" })
  | (ConfigSchedule & { type: "schedule" });

export type ArgField = FieldDisplay & { arg: string };

// A component that draws the whole body instead of the declared fields.
export type ApprovalRenderer =
  | "workflow_plan"
  | "agent_changes"
  | "capability_pins"
  | "new_agent"
  | "new_trigger"
  | "file_import";

export const APPROVAL_RENDERERS: readonly ApprovalRenderer[] = [
  "workflow_plan",
  "agent_changes",
  "capability_pins",
  "new_agent",
  "new_trigger",
  "file_import",
];

// As ``noxus.config.ToolApproval`` declares it.
export interface ToolApproval {
  op?: StepOp;
  entity?: EntityKind;
  id_arg?: string;
  name_arg?: string;
  // A display name that wins over ``name_arg`` when the call passes it.
  label_arg?: string;
  // A number of entities the heading counts.
  count_arg?: string;
  rename_arg?: string;
  // The arg holding the entity the heading's lives in.
  parent_arg?: string;
  // Arg -> the field of the entity's current record it changes.
  replaces?: Record<string, string>;
  warning?: string;
  // In card order: a list, since a stored pending approval (JSONB) does not
  // keep object key order.
  fields?: ArgField[];
  results?: ArgField[];
  renderer?: ApprovalRenderer;
}

export const CANNOT_UNDO = "This cannot be undone.";

function textArg(
  input: Record<string, unknown>,
  key: string | undefined
): string | undefined {
  const value = key ? input[key] : undefined;
  return typeof value === "string" && value ? value : undefined;
}

function listLength(
  input: Record<string, unknown>,
  key: string | undefined
): number | undefined {
  const value = key ? input[key] : undefined;
  return Array.isArray(value) ? value.length : undefined;
}

function countArg(
  input: Record<string, unknown>,
  key: string | undefined
): number | undefined {
  const value = key ? input[key] : undefined;
  return typeof value === "number" && Number.isInteger(value)
    ? value
    : undefined;
}

// What the card draws for one call. A destructive tool (its server's hint)
// warns that it cannot be undone unless the declaration says what is lost.
export function viewFromApproval(
  approval: ToolApproval,
  input: Record<string, unknown>,
  destructive: boolean,
  createdId?: string
): ApprovalView {
  // A name arg may hold an id (a tool that takes a name or an id): an id is
  // looked up like ``id_arg``, so the card never shows it raw.
  const named = textArg(input, approval.name_arg);
  const namedId = named && UUID_RE.test(named) ? named : undefined;
  return {
    op: approval.op,
    entity: approval.entity
      ? {
          kind: approval.entity,
          id:
            textArg(input, approval.id_arg) ??
            namedId ??
            (approval.op === "add" ? createdId : undefined),
          name:
            textArg(input, approval.label_arg) ?? (namedId ? undefined : named),
          count:
            listLength(input, approval.name_arg) ??
            countArg(input, approval.count_arg),
          parent: textArg(input, approval.parent_arg),
        }
      : undefined,
    warning: approval.warning ?? (destructive ? CANNOT_UNDO : undefined),
    rename_arg: approval.rename_arg,
    parent_arg: approval.parent_arg,
    replaces: approval.replaces,
    fields: approval.fields,
    results: approval.results,
    renderer: approval.renderer,
  };
}

export interface ApprovalView {
  op?: StepOp;
  entity?: EntityRef;
  // Set by the declaration, or by the tool's destructive hint.
  warning?: string;
  // The arg holding the entity's new name; the card strikes the current one.
  rename_arg?: string;
  // The arg holding the entity the heading's lives in.
  parent_arg?: string;
  // Arg -> the field of the entity's current record it changes: the card
  // strikes the current value before the new one.
  replaces?: Record<string, string>;
  // Each arg with its display, in the order the card lists them.
  fields?: ArgField[];
  results?: ArgField[];
  renderer?: ApprovalRenderer;
}

// The kinds with a page of their own, and those that open inside the page
// of the entity they live in.
const OWN_PAGE = new Set<EntityKind>([
  "agent",
  "knowledge_base",
  "table",
  "workflow",
  "conversation",
  "test_suite",
  "inbox",
  "case",
]);
const PAGE_IN_PARENT = new Set<EntityKind>([
  "version",
  "deployment",
  "column",
  "run",
  "test_case",
  "evaluation_run",
  "sync",
]);

// What an entity that lives in another lives in.
const PARENT_KIND: Partial<Record<EntityKind, EntityKind>> = {
  version: "agent",
  deployment: "agent",
  column: "table",
  rows: "table",
  document: "knowledge_base",
  run: "workflow",
  test_case: "test_suite",
  evaluation_run: "test_suite",
  sync: "knowledge_base",
  property: "inbox",
  property_group: "inbox",
};

// Kinds whose one page lists every one of them, so it opens with no id.
const LISTED_PAGE = new Set<EntityKind>(["usage_limit"]);

function opensPage(ref: EntityRef): boolean {
  if (LISTED_PAGE.has(ref.kind)) return true;
  if (!ref.id) return false;
  return (
    OWN_PAGE.has(ref.kind) || (PAGE_IN_PARENT.has(ref.kind) && !!ref.parent)
  );
}

function entityRefs(
  displays: ArgField[] | undefined,
  values: Record<string, unknown>,
  parents: Record<string, unknown>
): EntityRef[] {
  return (displays ?? []).flatMap((display) =>
    display.type === "entity_ref"
      ? [
          {
            kind: display.kind,
            id: textArg(values, display.arg),
            parent: textArg(parents, display.parent_arg ?? undefined),
          },
        ]
      : []
  );
}

// Where a call that ran changed something: what it returned (a copy, a run),
// else what it acted on, else what it acted in. A thing it removed has no
// page left; the place it was removed from does.
export function rowTarget(
  view: ApprovalView,
  { input, output }: { input: unknown; output: unknown }
): EntityRef | undefined {
  const args = isRecord(input) ? input : {};
  const returned = isRecord(output) ? output : {};
  // A dry run rolled back what it made: there is nothing to open.
  if (returned.dry_run === true) return undefined;
  const entity = view.entity;
  const parentKind = entity && PARENT_KIND[entity.kind];
  // What has no page of its own opens where it lives (a property, its inbox).
  const heading = !entity
    ? []
    : [
        ...(view.op !== "remove" ? [entity] : []),
        ...(parentKind && entity.parent
          ? [{ kind: parentKind, id: entity.parent }]
          : []),
      ];
  return [
    ...entityRefs(view.results, returned, { ...args, ...returned }),
    ...heading,
    ...entityRefs(view.fields, args, args),
  ].find(opensPage);
}
